import { useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { allVenues } from "../../mocks/allVenues";
import type { VenueDetail } from "../../mocks/allVenues";
import { getCoordsById } from "../../mocks/venueCoords";
import MapSidebar from "./components/MapSidebar";
import SelectedVenuePopup from "./components/SelectedVenuePopup";
import Navbar from "../home/components/Navbar";

const BURSA_DEFAULT_EMBED =
  "https://maps.google.com/maps?q=Bursa+Turkey+city+center+Osmangazi&output=embed&hl=tr&z=13";

const STATS = [
  { icon: "ri-map-pin-2-fill", value: "36", label: "Mekan" },
  { icon: "ri-ancient-pavilion-line", value: "7", label: "Kategori" },
  { icon: "ri-star-fill", value: "4.6", label: "Ort. Puan" },
  { icon: "ri-road-map-line", value: "5", label: "Hazır Rota" },
];

export default function MapPage() {
  const [selectedVenue, setSelectedVenue] = useState<VenueDetail | null>(null);
  const [mapSrc, setMapSrc] = useState(BURSA_DEFAULT_EMBED);
  const [mapLoading, setMapLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const mapIframeRef = useRef<HTMLIFrameElement>(null);

  const handleSelectVenue = useCallback((venue: VenueDetail) => {
    if (selectedVenue?.id === venue.id) {
      setSelectedVenue(null);
      setMapSrc(BURSA_DEFAULT_EMBED);
      return;
    }
    setSelectedVenue(venue);
    setMapLoading(true);
    const coords = getCoordsById(venue.id);
    const query = coords?.mapQuery || encodeURIComponent(`${venue.name} ${venue.address}`);
    setMapSrc(`https://maps.google.com/maps?q=${query}&output=embed&hl=tr&z=16`);
  }, [selectedVenue]);

  const handleClose = useCallback(() => {
    setSelectedVenue(null);
    setMapSrc(BURSA_DEFAULT_EMBED);
  }, []);

  const handleDirections = useCallback(() => {
    if (!selectedVenue) return;
    const coords = getCoordsById(selectedVenue.id);
    if (coords) {
      window.open(
        `https://www.google.com/maps/dir/?api=1&destination=${coords.lat},${coords.lng}`,
        "_blank"
      );
    } else {
      window.open(
        `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(selectedVenue.name + " " + selectedVenue.address)}`,
        "_blank"
      );
    }
  }, [selectedVenue]);

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-white">
      <Navbar />

      {/* Page below Navbar */}
      <div className="flex-1 flex flex-col overflow-hidden" style={{ paddingTop: "64px" }}>
        {/* Top Bar */}
        <div className="flex-shrink-0 bg-white border-b border-stone-100 px-4 md:px-6 py-2.5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="w-8 h-8 flex items-center justify-center rounded-lg border border-stone-200 hover:bg-stone-50 transition-colors cursor-pointer text-stone-500"
              >
                <i className={`${sidebarOpen ? "ri-layout-left-line" : "ri-layout-right-line"} text-sm`} />
              </button>
              <h2 className="text-sm font-bold text-anthracite-900 whitespace-nowrap">Bursa Harita Rehberi</h2>
            </div>

            <div className="hidden sm:flex items-center gap-3">
              {STATS.map((s) => (
                <div key={s.label} className="flex items-center gap-1.5">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`${s.icon} text-forest-600 text-sm`} />
                  </div>
                  <span className="text-xs font-semibold text-anthracite-800">{s.value}</span>
                  <span className="text-xs text-stone-500">{s.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Link
              to="/routes"
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-stone-600 hover:text-anthracite-800 border border-stone-200 hover:border-stone-300 rounded-lg transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-road-map-line text-sm" />
              <span className="hidden sm:inline">Rotalar</span>
            </Link>
            <button
              onClick={() => { setSelectedVenue(null); setMapSrc(BURSA_DEFAULT_EMBED); }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-forest-600 hover:bg-forest-500 rounded-lg transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-focus-3-line text-sm" />
              <span className="hidden sm:inline">Bursa Merkezi</span>
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Sidebar */}
          {sidebarOpen && (
            <MapSidebar
              venues={allVenues}
              selectedVenueId={selectedVenue?.id ?? null}
              onSelectVenue={handleSelectVenue}
            />
          )}

          {/* Map Area */}
          <div className="flex-1 relative min-w-0">
            {/* Map Loading Overlay */}
            {mapLoading && (
              <div className="absolute inset-0 z-10 bg-white/60 backdrop-blur-sm flex items-center justify-center pointer-events-none">
                <div className="flex flex-col items-center gap-3">
                  <div className="w-10 h-10 border-3 border-forest-600 border-t-transparent rounded-full animate-spin" />
                  <span className="text-sm text-stone-600 font-medium">Harita yükleniyor...</span>
                </div>
              </div>
            )}

            {/* Google Maps Iframe */}
            <iframe
              ref={mapIframeRef}
              key={mapSrc}
              src={mapSrc}
              title="Bursa Harita"
              className="w-full h-full border-0"
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              onLoad={() => setMapLoading(false)}
            />

            {/* Selected Venue Popup */}
            {selectedVenue && (
              <SelectedVenuePopup
                venue={selectedVenue}
                onClose={handleClose}
                onDirections={handleDirections}
              />
            )}

            {/* Map Attribution Banner */}
            {!selectedVenue && (
              <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 pointer-events-none">
                <div className="bg-white/90 backdrop-blur-sm rounded-full px-4 py-2 shadow-md border border-stone-100 flex items-center gap-2">
                  <i className="ri-map-pin-2-fill text-forest-600 text-sm" />
                  <span className="text-xs text-stone-600 font-medium whitespace-nowrap">
                    Listeden bir mekan seçerek haritada görün
                  </span>
                </div>
              </div>
            )}

            {/* Mobile sidebar toggle */}
            {!sidebarOpen && (
              <button
                onClick={() => setSidebarOpen(true)}
                className="absolute top-4 left-4 z-10 bg-white shadow-md rounded-xl px-3 py-2 flex items-center gap-2 text-sm font-medium text-anthracite-700 hover:bg-stone-50 transition-colors cursor-pointer border border-stone-200 whitespace-nowrap"
              >
                <i className="ri-list-check text-base" />
                Mekanlar
              </button>
            )}

            {/* Category legend */}
            <div className="absolute top-4 right-4 z-10 bg-white/95 backdrop-blur-sm rounded-xl shadow-md border border-stone-100 p-3 hidden lg:block">
              <p className="text-xs font-semibold text-anthracite-700 mb-2">Kategoriler</p>
              {[
                { key: "historical", label: "Tarihi", color: "bg-amber-400" },
                { key: "food", label: "Lezzet", color: "bg-red-400" },
                { key: "entertainment", label: "Eğlence", color: "bg-violet-400" },
                { key: "accommodation", label: "Konaklama", color: "bg-sky-400" },
                { key: "nature", label: "Doğa", color: "bg-emerald-400" },
                { key: "sightseeing", label: "Gezi", color: "bg-orange-400" },
              ].map((cat) => (
                <div key={cat.key} className="flex items-center gap-2 mb-1.5 last:mb-0">
                  <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${cat.color}`} />
                  <span className="text-xs text-stone-600">{cat.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
